/**
 *
 * Portal
 *
 */

import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, useTheme } from '@mui/material';
import PortalFooter from 'app/components/PortalFooter';
import PortalAppBar from 'app/components/PortalAppBar';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ImportData } from 'app/containers/ImportData';
import {
  selectAuthProvider,
  selectUserData,
} from 'app/providers/AuthProvider/slice/selectors';
import { selectImportDataStatusData } from 'app/containers/ImportData/slice/selectors';
import { Report } from '../Report';
import { selectCurrentCustomers } from '../CustomerSelector/slice/selectors';
import { TabbedContent } from '../../components/TabbedContent';
import { AmendedItems } from '../AmendedItems';
import { Dashboard } from '../Dashboard';
import { FullReport } from '../FullReport';
import { AdjustmentReports } from '../AdjustmentReports';
import { Customers } from '../Customers';
import { Users } from '../Users';
import { CustomerSelector } from '../CustomerSelector';
import { LineAdjustmentManager } from '../LineAdjustmentManager';
import { UserPermission } from 'types/types';
import { hasPermission } from '../../../utils/user';
import { usePageTracking } from '../../../utils/usePageTracking';
import { ManageSuppliers } from '../ManageSuppliers';
import { portalActions, usePortalSlice } from './slice';
import { ImportMassAdjustment } from '../ImportMassAdjustment';
import { ProfilePage } from '../ProfilePage';
import { Nag } from '../../components/Nag';
import { useLocalStorage } from 'usehooks-ts';
import { now } from 'lodash';

const basePath = '/portal';

interface Props {}

export function Portal(props: Props) {
  const theme = useTheme();
  const classes = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
    },
    content: {
      flex: '1 1 auto',
      position: 'relative',
      display: 'flex',
      backgroundColor: theme.palette.grey[300],
    },
    header: {
      flex: '0 1 auto',
    },
    footer: {
      flex: '0 1 auto',
      [theme.breakpoints.down('lg')]: {
        display: 'none',
      },
    },
  };
  const dispatch = useDispatch();
  const [nagDismissed, setNagDismissed] = useLocalStorage<number>(
    'nagDismissed',
    0,
  );
  const { pathname } = useLocation();
  const { actions } = usePortalSlice();
  const [route, setRoute] = React.useState({
    to: pathname,
    from: pathname,
  });

  const setMenu = (openState: boolean) =>
    dispatch(portalActions.setMenuOpen(openState));

  const userData = useSelector(selectUserData);
  const { loadUser } = useSelector(selectAuthProvider);
  const securityPath = '/portal/profile/security';

  // Set nag date to 7 days from now
  const onClose = () => setNagDismissed(now() + 60 * 60 * 24 * 7 * 1000);

  const showMfa: boolean = useMemo<boolean>(() => {
    if (!loadUser.data) return false;
    if (!loadUser.data.show_mfa_nag) return false;
    if (location.pathname === securityPath) {
      return false;
    }
    if (nagDismissed > now() && !loadUser.data.enforce_mfa) {
      return false;
    }

    return !loadUser.data.mfa_method;
  }, [nagDismissed, securityPath, loadUser.data, location.pathname]);

  const importDataStatus = useSelector(selectImportDataStatusData);
  const currentCustomers = useSelector(selectCurrentCustomers);
  usePageTracking();

  useEffect(() => {
    setMenu(false);
    setRoute(prev => ({ to: pathname, from: prev.to }));
  }, [pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (userData?.id) {
      window.Beamer.update({
        user_id: userData?.id,
        user_email: userData?.email,
        user_firstname: userData?.first_name,
        user_lastname: userData?.last_name,
      });
    }
  }, [userData?.id]);

  return (
    <CustomerSelector>
      <LineAdjustmentManager>
        <>
          <Helmet>
            <title>Portal</title>
            <meta name="description" content="Description of Portal" />
          </Helmet>
          <PortalAppBar
            importDataStatus={importDataStatus}
            userData={userData!}
            currentCustomers={currentCustomers}
          />
          <>
            <Nag
              onClose={
                loadUser.data && loadUser.data.enforce_mfa ? undefined : onClose
              }
              isEnabled={showMfa}
              path={securityPath}
              title="Set up 2-factor authentication to secure your account now"
            />
          </>

          <div>
            <Box sx={classes.container}>
              <Box sx={classes.header} />
              <Box sx={classes.content}>
                <Box width={'100%'}>
                  <Routes>
                    <Route path={''} element={<Dashboard />} />
                    <Route path={`report/full/:id`} element={<FullReport />} />
                    <Route
                      path={`report/*`}
                      element={
                        <TabbedContent
                          basePath={'/portal/report/'}
                          tabs={[
                            {
                              default: true,
                              label: 'All Reports',
                              path: 'all',
                              element: (
                                <Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'flex-start',
                                      mt: 4,
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant={'h1'}
                                      color={'primary'}
                                    >
                                      All reports
                                    </Typography>
                                  </Box>
                                  <Report id={'main-report'} type={'full'} />
                                </Box>
                              ),
                            },
                            {
                              label: 'Amended Items',
                              path: 'amended-items',
                              hidden: currentCustomers.length !== 1,
                              element: <AmendedItems />,
                            },
                            {
                              label: 'Pending Approval',
                              path: 'pending-approval',
                              hidden: currentCustomers.length !== 1,
                              element: (
                                <AdjustmentReports
                                  status={'pending approval'}
                                />
                              ),
                            },
                            {
                              label: 'Accepted Reports',
                              path: 'accepted',
                              hidden: currentCustomers.length !== 1,
                              element: (
                                <AdjustmentReports status={'accepted'} />
                              ),
                            },
                            {
                              label: 'Import Mass Adjustment',
                              path: 'import',
                              hidden: currentCustomers.length !== 1,
                              element: <ImportMassAdjustment />,
                            },
                          ]}
                        />
                      }
                    />
                    <Route
                      path={`accounts/*`}
                      element={
                        <TabbedContent
                          basePath={`${basePath}/accounts`}
                          tabs={[
                            {
                              label: 'Customers',
                              path: '/customers',
                              hidden: !hasPermission(
                                userData,
                                UserPermission.ADMINISTER_SYSTEM,
                              ),
                              element: <Customers />,
                              default: true,
                            },
                            {
                              label: 'Users',
                              path: '/users',
                              hidden: !hasPermission(
                                userData,
                                UserPermission.ADMINISTER_SYSTEM,
                              ),
                              element: <Users />,
                            },
                          ]}
                        />
                      }
                    />
                    <Route path={`profile/*`} element={<ProfilePage />} />
                    <Route
                      path={`suppliers/*`}
                      element={
                        currentCustomers.length === 1 &&
                        hasPermission(userData, UserPermission.UPLOAD_DATA) && (
                          <ManageSuppliers />
                        )
                      }
                    />
                    <Route
                      path={`import/*`}
                      element={
                        currentCustomers.length === 1 &&
                        hasPermission(userData, UserPermission.UPLOAD_DATA) && (
                          <ImportData />
                        )
                      }
                    />
                    <Route element={<Navigate to={basePath} />} />
                  </Routes>
                </Box>
              </Box>
              <Box sx={classes.footer}>
                <PortalFooter
                  userData={userData!}
                  currentCustomers={currentCustomers}
                />
              </Box>
            </Box>
          </div>
        </>
      </LineAdjustmentManager>
    </CustomerSelector>
  );
}
